import React from "react";
import {
  FiDollarSign,
  FiCreditCard,
  FiZap,
  FiGift,
  FiInfo,
  FiPlus,
  FiTrash2,
} from "react-icons/fi";

const AVAILABLE_CURRENCIES = [
  { code: "usd", label: "USD - US Dollar" },
  { code: "brl", label: "BRL - Brazilian Real" },
  { code: "eur", label: "EUR - Euro" },
  { code: "gbp", label: "GBP - British Pound" },
];

const MODELS = [
  {
    id: "freemium",
    name: "Freemium",
    icon: FiGift,
    description: "Free tier with limited features, upgrade options available",
    color: "var(--success)",
  },
  {
    id: "pro_one_time",
    name: "Pro (One-Time)",
    icon: FiDollarSign,
    description: "Single lifetime payment for all premium features",
    color: "#3b82f6",
  },
  {
    id: "subscription",
    name: "Subscription",
    icon: FiCreditCard,
    description: "Recurring billing (monthly/yearly) with full access",
    color: "var(--primary)",
  },
  {
    id: "token_based",
    name: "Token-Based (AI)",
    icon: FiZap,
    description: "Usage credits that reset monthly for AI features",
    color: "var(--warning)",
  },
];

/**
 * PricingModelSelector Component
 *
 * Simplified version - supports single model selection
 *
 * Props:
 *   selectedModel: string - Currently selected model ID
 *   onModelChange: function - Called when model selection changes
 *   config: object - Current configuration for the selected model
 *   onConfigChange: function - Called when configuration changes
 */
function PricingModelSelector({
  selectedModel = "",
  onModelChange,
  config = {},
  onConfigChange,
}) {
  const handleFieldChange = (field, value) => {
    if (!onConfigChange) {
      console.error("onConfigChange is not defined");
      return;
    }
    onConfigChange({ ...config, [field]: value });
  };

  const handlePriceChange = (index, field, value) => {
    if (!onConfigChange) return;
    const prices = [...(config.prices || [{ currency: "usd" }])];
    prices[index] = { ...prices[index], [field]: value };
    onConfigChange({ ...config, prices });
  };

  const addCurrency = () => {
    if (!onConfigChange) return;
    const prices = [...(config.prices || [{ currency: "usd" }])];
    const usedCurrencies = prices.map((p) => p.currency);
    const nextCurrency = AVAILABLE_CURRENCIES.find(
      (c) => !usedCurrencies.includes(c.code),
    );
    if (nextCurrency) {
      prices.push({ currency: nextCurrency.code });
      onConfigChange({ ...config, prices });
    }
  };

  const removeCurrency = (index) => {
    if (!onConfigChange) return;
    const prices = [...(config.prices || [])];
    if (prices.length <= 1) return;
    prices.splice(index, 1);
    onConfigChange({ ...config, prices });
  };

  // If no model selected, just show the selection cards
  if (!selectedModel) {
    return (
      <div>
        <h3 style={{ marginBottom: "16px" }}>Select Pricing Model</h3>
        <div className="grid grid-2" style={{ marginBottom: "16px" }}>
          {MODELS.map((model) => {
            const Icon = model.icon;
            const isSelected = selectedModel === model.id;

            return (
              <div
                key={model.id}
                onClick={() => onModelChange && onModelChange(model.id)}
                style={{
                  padding: "16px",
                  border: `2px solid ${
                    isSelected ? model.color : "var(--border-color)"
                  }`,
                  borderRadius: "8px",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                  background: isSelected ? "var(--bg-tertiary)" : "transparent",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "12px",
                  }}
                >
                  <input
                    type="radio"
                    name="pricingModel"
                    checked={isSelected}
                    onChange={() => onModelChange && onModelChange(model.id)}
                    onClick={(e) => e.stopPropagation()}
                    style={{ marginTop: "4px" }}
                  />
                  <div
                    style={{
                      padding: "8px",
                      borderRadius: "8px",
                      background: isSelected
                        ? "var(--bg-secondary)"
                        : "var(--bg-tertiary)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon
                      style={{
                        fontSize: "18px",
                        color: isSelected
                          ? model.color
                          : "var(--text-secondary)",
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "4px",
                      }}
                    >
                      <h4 style={{ fontWeight: 600, margin: 0 }}>
                        {model.name}
                      </h4>
                      {isSelected && (
                        <div
                          style={{
                            width: "8px",
                            height: "8px",
                            borderRadius: "50%",
                            background: model.color,
                          }}
                        />
                      )}
                    </div>
                    <p
                      style={{
                        fontSize: "13px",
                        color: "var(--text-secondary)",
                        margin: 0,
                      }}
                    >
                      {model.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Find the selected model
  const model = MODELS.find((m) => m.id === selectedModel);
  if (!model) return null;

  const prices = config.prices || [{ currency: "usd" }];
  const usedCurrencies = prices.map((p) => p.currency);
  const canAddCurrency = AVAILABLE_CURRENCIES.some(
    (c) => !usedCurrencies.includes(c.code),
  );

  return (
    <div>
      <div
        style={{
          background: "var(--bg-tertiary)",
          padding: "16px",
          borderRadius: "8px",
          border: `1px solid ${model.color}33`,
          marginTop: "16px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            marginBottom: "16px",
          }}
        >
          <div
            style={{
              padding: "8px",
              borderRadius: "8px",
              background: "var(--bg-secondary)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <model.icon style={{ fontSize: "18px", color: model.color }} />
          </div>
          <h4 style={{ fontWeight: 600, margin: 0 }}>
            {model.name} Configuration
          </h4>
          <button
            onClick={() => onModelChange && onModelChange("")}
            style={{
              marginLeft: "auto",
              background: "none",
              border: "1px solid var(--border-color)",
              borderRadius: "4px",
              padding: "4px 8px",
              fontSize: "12px",
              cursor: "pointer",
            }}
          >
            Change Model
          </button>
        </div>

        {model.id === "freemium" && (
          <div
            className="alert"
            style={{
              background: "rgba(59, 130, 246, 0.1)",
              border: "1px solid rgba(59, 130, 246, 0.3)",
              borderRadius: "8px",
              padding: "12px 16px",
              color: "#60a5fa",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <FiInfo />
            <span>
              Select which permissions to include in the free tier on the next
              section below.
            </span>
          </div>
        )}

        {model.id === "pro_one_time" && (
          <PriceTable
            prices={prices}
            usedCurrencies={usedCurrencies}
            canAddCurrency={canAddCurrency}
            showMonthly={false}
            showYearly={false}
            showAmount={true}
            onPriceChange={handlePriceChange}
            onAddCurrency={addCurrency}
            onRemoveCurrency={removeCurrency}
          />
        )}

        {(model.id === "subscription" || model.id === "token_based") && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            {/* Billing Intervals */}
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Billing Intervals</label>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={(config.intervals || ["monthly"]).includes(
                      "monthly",
                    )}
                    onChange={(e) => {
                      const intervals = config.intervals || ["monthly"];
                      const next = e.target.checked
                        ? [...new Set([...intervals, "monthly"])]
                        : intervals.filter((i) => i !== "monthly");
                      handleFieldChange("intervals", next);
                    }}
                  />
                  <span>Monthly</span>
                </label>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={(config.intervals || []).includes("yearly")}
                    onChange={(e) => {
                      const intervals = config.intervals || ["monthly"];
                      const next = e.target.checked
                        ? [...new Set([...intervals, "yearly"])]
                        : intervals.filter((i) => i !== "yearly");
                      handleFieldChange("intervals", next);
                    }}
                  />
                  <span>Yearly (typically 2 months free)</span>
                </label>
              </div>
            </div>

            <PriceTable
              prices={prices}
              usedCurrencies={usedCurrencies}
              canAddCurrency={canAddCurrency}
              showMonthly={(config.intervals || ["monthly"]).includes(
                "monthly",
              )}
              showYearly={(config.intervals || []).includes("yearly")}
              showAmount={false}
              onPriceChange={handlePriceChange}
              onAddCurrency={addCurrency}
              onRemoveCurrency={removeCurrency}
            />

            {model.id === "token_based" && (
              <>
                <div
                  style={{
                    borderTop: "1px solid var(--border-color)",
                    paddingTop: "16px",
                  }}
                >
                  <h5
                    style={{
                      fontWeight: 600,
                      marginBottom: "8px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <FiZap style={{ color: "var(--warning)" }} />
                    Token Allowance
                  </h5>
                  <input
                    type="number"
                    min="0"
                    value={config.tokenAllowance || ""}
                    onChange={(e) =>
                      handleFieldChange(
                        "tokenAllowance",
                        parseInt(e.target.value) || 0,
                      )
                    }
                    placeholder="1000"
                    className="form-input"
                  />
                  <small
                    style={{
                      fontSize: "12px",
                      color: "var(--text-secondary)",
                      marginTop: "4px",
                      display: "block",
                    }}
                  >
                    Number of tokens/credits per billing period
                  </small>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Reusable price table for multi-currency entries
 */
function PriceTable({
  prices,
  usedCurrencies,
  canAddCurrency,
  showMonthly,
  showYearly,
  showAmount,
  onPriceChange,
  onAddCurrency,
  onRemoveCurrency,
}) {
  return (
    <div>
      <label
        className="form-label"
        style={{ marginBottom: "8px", display: "block" }}
      >
        Prices by Currency
      </label>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: "14px",
        }}
      >
        <thead>
          <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
            <th style={{ textAlign: "left", padding: "8px" }}>Currency</th>
            {showAmount && (
              <th style={{ textAlign: "left", padding: "8px" }}>
                Price (cents)
              </th>
            )}
            {showMonthly && (
              <th style={{ textAlign: "left", padding: "8px" }}>
                Monthly (cents)
              </th>
            )}
            {showYearly && (
              <th style={{ textAlign: "left", padding: "8px" }}>
                Yearly (cents)
              </th>
            )}
            <th style={{ width: "40px" }}></th>
          </tr>
        </thead>
        <tbody>
          {prices.map((entry, idx) => (
            <tr key={idx}>
              <td style={{ padding: "6px 8px" }}>
                <select
                  value={entry.currency || "usd"}
                  onChange={(e) =>
                    onPriceChange(idx, "currency", e.target.value)
                  }
                  className="form-input"
                  style={{ minWidth: "160px" }}
                >
                  {AVAILABLE_CURRENCIES.filter(
                    (c) =>
                      c.code === entry.currency ||
                      !usedCurrencies.includes(c.code),
                  ).map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </td>
              {showAmount && (
                <td style={{ padding: "6px 8px" }}>
                  <input
                    type="number"
                    min="0"
                    value={entry.amount || ""}
                    onChange={(e) =>
                      onPriceChange(
                        idx,
                        "amount",
                        parseInt(e.target.value) || 0,
                      )
                    }
                    placeholder="9900"
                    className="form-input"
                  />
                </td>
              )}
              {showMonthly && (
                <td style={{ padding: "6px 8px" }}>
                  <input
                    type="number"
                    min="0"
                    value={entry.monthlyAmount || ""}
                    onChange={(e) =>
                      onPriceChange(
                        idx,
                        "monthlyAmount",
                        parseInt(e.target.value) || 0,
                      )
                    }
                    placeholder="1900"
                    className="form-input"
                  />
                </td>
              )}
              {showYearly && (
                <td style={{ padding: "6px 8px" }}>
                  <input
                    type="number"
                    min="0"
                    value={entry.yearlyAmount || ""}
                    onChange={(e) =>
                      onPriceChange(
                        idx,
                        "yearlyAmount",
                        parseInt(e.target.value) || 0,
                      )
                    }
                    placeholder="19000"
                    className="form-input"
                  />
                </td>
              )}
              <td style={{ padding: "6px 8px", textAlign: "center" }}>
                {prices.length > 1 && (
                  <button
                    onClick={() => onRemoveCurrency(idx)}
                    style={{
                      background: "none",
                      border: "none",
                      color: "var(--error)",
                      cursor: "pointer",
                      padding: "4px",
                    }}
                    title="Remove currency"
                  >
                    <FiTrash2 />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {canAddCurrency && (
        <button
          onClick={onAddCurrency}
          style={{
            background: "none",
            border: "1px dashed var(--border-color)",
            borderRadius: "6px",
            padding: "8px 16px",
            color: "var(--primary)",
            cursor: "pointer",
            marginTop: "8px",
            fontSize: "13px",
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          <FiPlus /> Add Currency
        </button>
      )}
    </div>
  );
}

export default PricingModelSelector;
